/**
 * Patient search: first/last name are stored separately, so a single search box
 * with "Kiran S" maps to first_name ~ %Kiran% and last_name ~ %S%.
 *
 * For a single token (e.g. "Kiran"), apply it to both first_name and last_name.
 */
export const PATIENT_SEARCH_MATCH_ANY_SENTINEL = '__PATIENT_MATCH_ANY__';

export type PatientSearchFilter = {
  field: string;
  operator: 'contains';
  value: string;
};

/**
 * Build Refine `contains` filters for patient list/search-select/autocomplete.
 * - first_name: first whitespace-separated token
 * - last_name: remaining tokens joined; if none, full trimmed query
 * - id, mobile_no, email, etc.: full trimmed query
 */
export function buildPatientSearchFilters(
  searchValue: string,
  fields: string[]
): PatientSearchFilter[] {
  const trimmed = searchValue.trim();
  if (!trimmed) return [];

  const hasFirst = fields.includes('first_name');
  const hasLast = fields.includes('last_name');

  if (!hasFirst && !hasLast) {
    return fields.map(field => ({
      field,
      operator: 'contains' as const,
      value: trimmed,
    }));
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  const firstToken = parts[0] ?? '';
  const restTokens = parts.length > 1 ? parts.slice(1).join(' ') : '';

  return fields.map(field => {
    if (field === 'first_name') {
      // With last_name in query, first token is the given name; otherwise still use first token
      return { field, operator: 'contains' as const, value: firstToken };
    }
    if (field === 'last_name') {
      // last_name only (no first_name field): e.g. "Shah" must match last name, not wildcard
      if (!hasFirst) {
        const value =
          parts.length > 1 ? restTokens : trimmed;
        return { field, operator: 'contains' as const, value };
      }
      return {
        field,
        operator: 'contains' as const,
        value: restTokens || trimmed,
      };
    }
    return { field, operator: 'contains' as const, value: trimmed };
  });
}

/**
 * Values for export / legacy `search[field]` query params (raw values, no % — backend may add LIKE).
 * Sentinel becomes `%` so wildcard-only last_name matches any.
 */
export function getPatientSearchParamValues(
  searchValue: string,
  fields: string[]
): Record<string, string> {
  const filters = buildPatientSearchFilters(searchValue, fields);
  const out: Record<string, string> = {};
  for (const f of filters) {
    out[f.field] =
      f.value === PATIENT_SEARCH_MATCH_ANY_SENTINEL ? '%' : f.value;
  }
  return out;
}
